import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import { scaleQuantity } from '../utils/scaleQuantity';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RecipeImage from '../components/RecipeImage';
import {
    getActiveRecipes,
    getSessionEntry,
    addToSession,
    removeFromSession,
    clearSession,
    toggleIngredient,
    toggleDirection,
    setMultiplier,
} from '../utils/cookSession';

const SIDEBAR_WIDTH = 260;

const stepsProgress = (entry) => {
    if (!entry) return { done: 0, total: 0 };
    const total = entry.recipe?.directions?.length || 0;
    const done = entry.checkedDirections?.length || 0;
    return { done, total };
};

const RecipeSidebarItem = ({ entry, selected, onClick, onRemove }) => {
    const { done, total } = stepsProgress(entry);
    return (
        <ListItem disablePadding divider secondaryAction={
            <Tooltip title="Remove">
                <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(); }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        }>
            <ListItemButton
                onClick={onClick}
                selected={selected}
                sx={{ gap: 1.5, py: 1.5, pr: 6, borderLeft: selected ? '3px solid' : '3px solid transparent', borderColor: selected ? 'primary.main' : 'transparent' }}
            >
                <RecipeImage
                    src={entry.recipe.download_image_url || '/anchovie.jpg'}
                    alt={entry.recipe.title}
                    sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                />
                <ListItemText
                    primary={
                        <Typography variant="body2" fontWeight={selected ? 700 : 400} noWrap>
                            {entry.recipe.title}
                        </Typography>
                    }
                    secondary={
                        total > 0
                            ? `${total - done} of ${total} steps left`
                            : 'No steps'
                    }
                />
            </ListItemButton>
        </ListItem>
    );
};

const AddToSessionDialog = ({ open, onClose, activeIds }) => {
    const [query, setQuery] = useState('');

    const allRecipes = (() => {
        try {
            const cached = JSON.parse(sessionStorage.getItem('recipeList'));
            return Object.values(cached?.recipes || {}).flat();
        } catch {
            return [];
        }
    })();

    const results = query.trim()
        ? allRecipes.filter(r =>
            r.title &&
            r.title.toLowerCase().includes(query.toLowerCase())
          ).slice(0, 8)
        : [];

    return (
        <Dialog open={open} onClose={() => { setQuery(''); onClose(); }} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><OutdoorGrillIcon sx={{ color: 'primary.main' }} />Add to Cooking Session</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    placeholder="Search recipes..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <List disablePadding>
                    {results.map(r => {
                        const already = activeIds.has(String(r.id));
                        return (
                            <ListItem key={r.id} disablePadding divider>
                                <ListItemButton onClick={() => !already && addToSession(r)} sx={{ gap: 1.5 }}>
                                    <RecipeImage
                                        src={r.download_image_url || '/anchovie.jpg'}
                                        alt={r.title}
                                        sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                    />
                                    <ListItemText primary={r.title} />
                                    <Tooltip title={already ? 'Already in session' : 'Add to session'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={already}
                                                onClick={e => { e.stopPropagation(); if (!already) addToSession(r); }}
                                                sx={{ color: already ? 'success.main' : 'primary.main' }}
                                            >
                                                {already ? <CheckCircleIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                    {query.trim() && results.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No recipes found
                        </Typography>
                    )}
                </List>
            </DialogContent>
        </Dialog>
    );
};

const RecipePanel = ({ entries, selectedId, onSelect, onRemove, onClearAll, onAddOpen }) => {
    return (
    <Box sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                Cooking Now ({entries.length})
            </Typography>
            <Button size="small" color="error" sx={{ textTransform: 'none', minWidth: 0 }} onClick={onClearAll}>
                Clear all
            </Button>
        </Box>
        <List disablePadding>
            {entries.map(e => (
                <RecipeSidebarItem
                    key={e.recipe.id}
                    entry={e}
                    selected={String(e.recipe.id) === String(selectedId)}
                    onClick={() => onSelect(e.recipe.id)}
                    onRemove={() => onRemove(e.recipe.id)}
                />
            ))}
        </List>
        <Box sx={{ px: 2, pt: 1 }}>
            <Button
                size="small"
                startIcon={<AddIcon />}
                fullWidth
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={onAddOpen}
            >
                Add recipe
            </Button>
        </Box>
    </Box>
    );
};

const DoneButton = ({ onFinished }) => {
    const [finishing, setFinishing] = useState(false);
    const timerRef = useRef(null);

    const handleClick = () => {
        if (finishing) return;
        setFinishing(true);
        timerRef.current = setTimeout(() => onFinished(), 600);
    };

    useEffect(() => () => clearTimeout(timerRef.current), []);

    return (
        <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            startIcon={finishing ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
            sx={{
                flexShrink: 0,
                borderColor: finishing ? 'success.main' : 'text.disabled',
                color: finishing ? 'success.main' : 'text.secondary',
                transition: 'color 0.3s, border-color 0.3s',
                '&:hover': {
                    borderColor: 'success.main',
                    color: 'success.main',
                    backgroundColor: 'transparent',
                },
            }}
        >
            Done
        </Button>
    );
};

const CookView = ({ entry, onFinished }) => {
    const recipe = entry.recipe;
    const multiplier = entry.multiplier ?? 1;

    const checkedIngs = new Set(entry.checkedIngredients);
    const checkedDirs = new Set(entry.checkedDirections);

    const groupedIngredients = (recipe.ingredients || []).reduce((acc, ing) => {
        const cat = ing.category_name || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(ing);
        return acc;
    }, {});

    const groupedDirections = (recipe.directions || []).reduce((acc, dir) => {
        const cat = dir.category_name || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(dir);
        return acc;
    }, {});

    const sortedIngCategories = Object.entries(groupedIngredients).sort(([a, aI], [b, bI]) => {
        if (a === 'Uncategorized') return 1;
        if (b === 'Uncategorized') return -1;
        return (aI[0]?.category_order ?? 0) - (bI[0]?.category_order ?? 0);
    });

    const sortedDirCategories = Object.entries(groupedDirections).sort(([a, aD], [b, bD]) => {
        if (a === 'Uncategorized') return 1;
        if (b === 'Uncategorized') return -1;
        return (aD[0]?.category_order ?? 0) - (bD[0]?.category_order ?? 0);
    });

    const allDirsSorted = sortedDirCategories.flatMap(([, dirs]) =>
        dirs.slice().sort((x, y) => (x.direction_order ?? 0) - (y.direction_order ?? 0))
    );

    const allIngsSorted = sortedIngCategories.flatMap(([, ings]) => ings);

    return (
        <Box sx={{ pb: 12 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <RecipeImage
                    src={recipe.download_image_url || '/anchovie.jpg'}
                    alt={recipe.title}
                    sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>{recipe.title}</Typography>
                    {recipe.author && (
                        <Typography variant="body2" color="text.secondary" noWrap>{recipe.author}</Typography>
                    )}
                </Box>
                <DoneButton key={recipe.id} onFinished={onFinished} />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 5 }}>
                {/* Ingredients */}
                <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 280 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="h6">Ingredients</Typography>
                        <ToggleButtonGroup
                            value={multiplier}
                            exclusive
                            onChange={(_, val) => { if (val !== null) setMultiplier(recipe.id, val); }}
                            size="small"
                        >
                            <ToggleButton value={1}>1x</ToggleButton>
                            <ToggleButton value={2}>2x</ToggleButton>
                            <ToggleButton value={3}>3x</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {sortedIngCategories.map(([category, ings]) => (
                        <Box key={category} sx={{ mb: 3 }}>
                            {category !== 'Uncategorized' && (
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{category}</Typography>
                            )}
                            {ings.map((ing) => {
                                const globalIdx = allIngsSorted.indexOf(ing);
                                const checked = checkedIngs.has(globalIdx);
                                return (
                                    <FormControlLabel
                                        key={globalIdx}
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggleIngredient(recipe.id, globalIdx)}
                                                icon={<RadioButtonUncheckedIcon />}
                                                checkedIcon={<CheckCircleIcon />}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body1"
                                                sx={{ textDecoration: checked ? 'line-through' : 'none', color: checked ? 'text.disabled' : 'text.primary' }}
                                            >
                                                {[scaleQuantity(ing.quantity, multiplier), ing.unit, ing.ingredient].filter(Boolean).join(' ')}
                                            </Typography>
                                        }
                                        sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, mr: 0 }}
                                    />
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                {/* Directions */}
                {allDirsSorted.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>Directions</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {sortedDirCategories.map(([category, dirs]) => {
                            const sorted = dirs.slice().sort((x, y) => (x.direction_order ?? 0) - (y.direction_order ?? 0));
                            return (
                                <Box key={category} sx={{ mb: 3 }}>
                                    {category !== 'Uncategorized' && (
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{category}</Typography>
                                    )}
                                    {sorted.map((dir) => {
                                        const globalIdx = allDirsSorted.indexOf(dir);
                                        const checked = checkedDirs.has(globalIdx);
                                        return (
                                            <Box
                                                key={globalIdx}
                                                onClick={() => toggleDirection(recipe.id, globalIdx)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1.5,
                                                    mb: 2.5,
                                                    cursor: 'pointer',
                                                    opacity: checked ? 0.5 : 1,
                                                    transition: 'opacity 0.2s',
                                                }}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleDirection(recipe.id, globalIdx)}
                                                    onClick={e => e.stopPropagation()}
                                                    icon={<RadioButtonUncheckedIcon />}
                                                    checkedIcon={<CheckCircleIcon />}
                                                    size="small"
                                                    sx={{ mt: 0.25, flexShrink: 0 }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        lineHeight: 1.8,
                                                        textDecoration: checked ? 'line-through' : 'none',
                                                    }}
                                                >
                                                    <Box component="span" fontWeight={700} sx={{ mr: 0.5 }}>
                                                        {dir.direction_order ?? (globalIdx + 1)}.
                                                    </Box>
                                                    {dir.direction}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const CookPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const [entries, setEntries] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const hadRecipes = useRef(false);

    const refresh = useCallback(() => {
        const active = getActiveRecipes();
        setEntries(active);
        if (active.length === 0) {
            if (hadRecipes.current) navigate('/');
            return;
        }
        hadRecipes.current = true;
        setSelectedId(prev => {
            const stillActive = active.find(e => String(e.recipe.id) === String(prev));
            return stillActive ? prev : active[0].recipe.id;
        });
    }, [navigate]);

    useEffect(() => {
        refresh();
        if (getActiveRecipes().length === 0) setAddOpen(true);
        window.addEventListener('cookSessionChanged', refresh);
        return () => window.removeEventListener('cookSessionChanged', refresh);
    }, [refresh]);

    // Screen Wake Lock — keep screen on while cooking
    useEffect(() => {
        let wakeLock = null;
        if ('wakeLock' in navigator) {
            navigator.wakeLock.request('screen').then(wl => { wakeLock = wl; }).catch(() => {});
        }
        return () => { if (wakeLock) wakeLock.release().catch(() => {}); };
    }, []);

    const handleFinished = (id) => {
        removeFromSession(id);
        // refresh() fires via the cookSessionChanged event
    };

    const selectedEntry = selectedId ? getSessionEntry(selectedId) : null;
    const activeIds = new Set(entries.map(e => String(e.recipe.id)));

    const sidebar = <RecipePanel entries={entries} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setDrawerOpen(false); }} onRemove={removeFromSession} onClearAll={clearSession} onAddOpen={() => setAddOpen(true)} />;

    return (
        <>
            {/* Always-mounted dialog — must never move in the tree */}
            <AddToSessionDialog
                open={addOpen}
                activeIds={activeIds}
                onClose={() => { setAddOpen(false); if (entries.length === 0) navigate('/'); }}
            />

            {entries.length === 0 ? (
                <Container component="main" maxWidth="xs" sx={{ mt: 10, textAlign: 'center' }}>
                    <OutdoorGrillIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>No recipes in session</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Search for a recipe to get started.
                    </Typography>
                    <Button variant="contained" onClick={() => setAddOpen(true)}>Add Recipe</Button>
                </Container>
            ) : isMobile ? (
                <Container component="main" sx={{ mt: 4, pb: 12, width: '90%' }}>
                    {entries.length > 1 && (
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                icon={<MenuBookIcon />}
                                label={`${entries.length} recipes in session`}
                                onClick={() => setDrawerOpen(true)}
                                variant="outlined"
                                sx={{ px: 1 }}
                            />
                        </Box>
                    )}
                    {selectedEntry && (
                        <CookView
                            entry={selectedEntry}
                            onFinished={() => handleFinished(selectedId)}
                        />
                    )}
                    <Drawer
                        anchor="bottom"
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        PaperProps={{ sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, pb: 4, pt: 2, maxHeight: '70vh' } }}
                    >
                        {sidebar}
                    </Drawer>
                </Container>
            ) : (
                <Box sx={{ display: 'flex', mt: 4, pb: 12 }}>
                    <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', position: 'sticky', top: 64, height: 'calc(100vh - 64px)', overflowY: 'auto' }}>
                        {sidebar}
                    </Box>
                    <Container component="main" maxWidth="md" sx={{ flex: 1 }}>
                        {selectedEntry && (
                            <CookView
                                entry={selectedEntry}
                                onFinished={() => handleFinished(selectedId)}
                            />
                        )}
                    </Container>
                </Box>
            )}
        </>
    );
};

export default CookPage;
